import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DeliveryChannel, DeliveryStatus } from "~/api/opsTypes";
import { useDeliveries } from "~/api/queries";
import { Pill } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatDateTime } from "~/foundation/utils/time";

const CHANNELS: DeliveryChannel[] = ["app", "email", "chat", "webhook"];
const STATUSES: DeliveryStatus[] = ["sent", "failed", "skipped"];
const PAGE_SIZE = 25;

/** "Who was told what, when, on which channel" — the corporate-audit answer. */
export function DeliveryLedger() {
  const { t, i18n } = useTranslation();
  const [channel, setChannel] = useState<DeliveryChannel | "">("");
  const [status, setStatus] = useState<DeliveryStatus | "">("");
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isError, error } = useDeliveries({
    ...(channel ? { channel } : {}),
    ...(status ? { status } : {}),
    offset,
    limit: PAGE_SIZE,
  });

  const changeChannel = (value: string) => {
    setChannel(value as DeliveryChannel | "");
    setOffset(0);
  };
  const changeStatus = (value: string) => {
    setStatus(value as DeliveryStatus | "");
    setOffset(0);
  };

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "Every notification delivery attempt across channels — sent, failed, or explicitly skipped (preference, disabled channel). Admin-only.",
        )}
      </Text>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label={t("Filter by channel")}
          value={channel}
          onChange={(event) => changeChannel(event.target.value)}
        >
          <option value="">{t("All channels")}</option>
          {CHANNELS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t("Filter by status")}
          value={status}
          onChange={(event) => changeStatus(event.target.value)}
        >
          <option value="">{t("All statuses")}</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : isError ? (
        <Empty
          message={
            error instanceof Error ? error.message : t("Delivery history could not be loaded.")
          }
        />
      ) : data?.data.length ? (
        <>
          <div className="overflow-x-auto rounded-md border border-divider">
            <table className="w-full text-start text-body">
              <thead>
                <tr className="border-b border-divider text-start text-small text-text-tertiary">
                  <th className="px-3 py-2 text-start font-medium">{t("When")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Event")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Recipient")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Channel")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Status")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("Detail")}</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr key={row.id} className="border-b border-divider last:border-b-0">
                    <td className="whitespace-nowrap px-3 py-2 text-small text-text-tertiary">
                      {formatDateTime(row.attemptedAt, i18n.language)}
                    </td>
                    {/* A server-defined identifier — never translated. */}
                    <td className="px-3 py-2 font-mono text-small text-text">{row.event}</td>
                    <td className="px-3 py-2 text-text">
                      {row.userName ?? row.userEmail ?? row.userId ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.channel}</td>
                    <td className="px-3 py-2">
                      <Pill
                        tone={
                          row.status === "sent"
                            ? "accent"
                            : row.status === "failed"
                              ? "danger"
                              : "neutral"
                        }
                      >
                        {row.status}
                      </Pill>
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-small text-text-tertiary">
                      {row.error ?? row.detail ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              {t("Previous")}
            </Button>
            <Text variant="small">
              {t("{{page}} / {{pages}} · {{total}}", { page, pages, total })}
            </Text>
            <Button
              size="sm"
              disabled={page >= pages}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              {t("Next")}
            </Button>
          </div>
        </>
      ) : (
        <Empty message={t("Nothing has been delivered yet.")} />
      )}
    </div>
  );
}
